import React from "react";
import { Link } from "react-router-dom";
import RoboticsShell from "./RoboticsShell";
import { useAuth } from "../../lib/auth";

/**
 * Dashboard — Phase 2 shell.
 *
 *   - user === null   → hydrating from /auth/me (avoid flashing anon UI)
 *   - user === false  → anonymous → preview banner + greyed sample cards
 *   - user object     → signed-in → greeting + metrics + sign-out
 *
 * Signed-in metrics stay zeroed because Phase 3 wires in real progress.
 * No new backend endpoints are called in this fix — we lean on the shared
 * AuthProvider (STEAM's JWT under the hood, invisible to the student).
 */
export default function RoboticsDashboard() {
  const { user, logout } = useAuth();
  const loading   = user === null;
  const anonymous = user === false;
  const signedIn  = !loading && !anonymous;

  const firstName = signedIn
    ? (user.first_name || user.name || (user.email || "").split("@")[0] || "engineer")
    : "";

  return (
    <RoboticsShell>
      <section className="rbx-page" data-testid="rbx-page-dashboard">
        <span className="rbx-eyebrow" data-testid="rbx-dashboard-eyebrow">
          {signedIn
            ? `Section · Welcome to the workbench, ${firstName}`
            : "Section · Student workbench"}
        </span>
        <h1 className="rbx-page__title" data-testid="rbx-dashboard-title">
          Your bench, your log.
        </h1>
        <p className="rbx-page__lede">
          Progress log, capstone submissions, workbook downloads and simulator
          bookmarks live here. Phase 3 wires in real progress.
        </p>

        {anonymous && (
          <div className="rbx-preview-banner" data-testid="rbx-preview-banner">
            <div className="rbx-preview-banner__text">
              Here&rsquo;s what students see.
              <small>Sign up to save progress, download workbooks, and file capstones.</small>
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <Link className="rbx-btn rbx-btn--primary" to="/robotics/signup" data-testid="rbx-preview-signup">
                Create an account
              </Link>
              <Link className="rbx-btn rbx-btn--ghost" to="/robotics/signin" data-testid="rbx-preview-signin">
                Sign in
              </Link>
            </div>
          </div>
        )}

        {signedIn && (
          <div className="rbx-preview-banner" data-testid="rbx-signedin-banner"
               style={{ background: "linear-gradient(90deg, rgba(46,229,157,0.10) 0%, rgba(244,196,106,0.06) 100%)" }}>
            <div className="rbx-preview-banner__text" style={{ color: "var(--rbx-phosphor)" }}>
              Signed in as <span style={{ color: "var(--rbx-gold)" }}>{user.email}</span>.
              <small>Real progress lands in Phase 3. Nothing to file yet.</small>
            </div>
            <button
              type="button"
              className="rbx-btn rbx-btn--ghost"
              onClick={logout}
              data-testid="rbx-signout-btn"
            >
              Sign out
            </button>
          </div>
        )}

        <div
          className={anonymous ? "rbx-greyed" : ""}
          style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 16, marginTop: 24 }}
          data-testid="rbx-dashboard-metrics"
        >
          {METRICS.map((m) => (
            <div key={m.label} className="rbx-card" data-testid={`rbx-dashboard-metric-${m.label.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="rbx-band__num">{m.label}</div>
              <div className="rbx-band__age" style={{ fontFamily: "var(--rbx-font-mono)", fontSize: 34, letterSpacing: 0 }}>
                {m.value}
              </div>
              <div className="rbx-band__name" style={{ marginBottom: signedIn ? 10 : 0 }}>{m.hint}</div>
              {signedIn && (
                <span className="rbx-chip-q4" data-testid={`rbx-metric-chip-${m.label.toLowerCase().replace(/\s+/g, "-")}`}>
                  Phase 3: content coming
                </span>
              )}
            </div>
          ))}
        </div>

        {/* Anonymous visitors see sample-lesson placeholders. Signed-in users
            get the same tiles ungreyed, framed as "coming next" previews.  */}
        <h2 className="rbx-page__title" style={{ fontSize: 22, marginTop: 48, marginBottom: 12 }}>
          {signedIn ? "Coming next" : "Sample lessons"}
        </h2>
        <div className={anonymous ? "rbx-greyed" : ""}
             style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 14 }}>
          {SAMPLE_LESSONS.map((L) => (
            <article key={L.id} className="rbx-card" data-testid={`rbx-dashboard-lesson-${L.id}`}>
              <div className="rbx-band__num">{L.tag}</div>
              <div className="rbx-band__age" style={{ fontSize: 18, margin: "6px 0" }}>{L.title}</div>
              <p className="rbx-band__blurb" style={{ fontSize: 13 }}>{L.blurb}</p>
            </article>
          ))}
        </div>

        {/* Bench blueprint quick link */}
        <div style={{ marginTop: 40 }}>
          <a
            className="rbx-btn rbx-btn--ghost"
            href="/api/downloads/robotics_workbench_blueprint.pdf"
            data-testid="rbx-dashboard-bench-link"
          >
            Download The Bench Blueprint
          </a>
        </div>
      </section>
    </RoboticsShell>
  );
}

const METRICS = [
  { label: "Lessons completed", value: "0 / 30", hint: "Explorer band" },
  { label: "Current tier",      value: "—",      hint: "Set in Phase 3" },
  { label: "Streak",            value: "0 days", hint: "Longest 0 days" },
];

const SAMPLE_LESSONS = [
  { id: "e1w1l1", tag: "EXPLORER · T1 · WEEK 1", title: "Welcome to the workbench", blurb: "Name six tools. Wear safety glasses. Understand a real lab is a real workshop." },
  { id: "e1w2l2", tag: "EXPLORER · T1 · WEEK 2", title: "Meet the LED",              blurb: "Electrons falling down an energy step release a photon. Wire your first diode." },
  { id: "e1w5l2", tag: "EXPLORER · T1 · CAPSTONE", title: "Reaction Timer Game",     blurb: "Micro:bit measures human reaction time. First real engineering artefact." },
];
